import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, Grid, Typography, TextField, InputAdornment, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import DashboardNavbar from '../../Components/DashboardNavbar/DashboardNavbar';
import CreateProjectModal from '../../Components/Modals/CreateProjectModal';
import EditProjectModal from '../../Components/Modals/EditProjectModal';
import EditClassModal from '../../Components/Modals/EditClassModal';
import DeleteConfirmModal from '../../Components/Modals/DeleteConfirmModal';
import ProjectDetailModal from '../../Components/Modals/ProjectDetailModal';

import { useLocation, useNavigate } from 'react-router-dom';
import EditStudentModal from '../../Components/Modals/EditStudentModal';
import CSVImport from '../../Components/FileManager/CSVImport';
import CSVExport from '../../Components/FileManager/CSVExport';

function ClassView(props) {
    const { state } = useLocation();
    const [selectedClass, setSelectedClass] = useState(state);
    const [projects, setProjects] = useState([]);
    const [searchFilter, setSearchFilter] = useState("");
    const [students, setStudents] = useState([]);
    const [listType, setListType] = useState('projects');

    const [selectedStudent, setSelectedStudent] = useState({ user_id: -1, first_name: '', last_name: '' });
    const [studentCurrTeam, setStudentCurrTeam] = useState();

    const [selectedProject, setSelectedProject] = useState({ project_id: -1, name: '', capacity: 0, sponsor: '', start_semester: 'Fall', end_semester: 'Spring', interdisciplinary: false, documents: [], student: false, funded: false });

    const [dynamicDeleteModalProps, setDynamicDeleteModalProps] = useState({
        type: '',
        content: '',
        onDelete: () => {}
    });
    
    const [importProjects, setImportProjects] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editClassOpen, setEditClassOpen] = useState(false);
    const [editStudentOpen, setEditStudentOpen] = useState(false);
    const [editProjectOpen, setEditProjectOpen] = useState(false);

    const [errorMessage, setErrorMessage] = useState("");

    const navigate = useNavigate();
    
    const handleListChange = (event, value) => {
        if(value == null){
            return
        }
        setListType(value);
    };
    
    const filteredProjects = useMemo(() => {
        return projects.filter(project => project.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        project.end_semester.toLowerCase().includes(searchFilter.toLowerCase()));
    }, [projects, searchFilter]);
    
    const filteredStudents = useMemo(() => {
        return students.filter(student => (student.first_name.toLowerCase() +  " " + 
        student.last_name.toLowerCase()).includes(searchFilter.toLowerCase()));
    }, [students, searchFilter]);

    const addProject = (project) => {
        setProjects([...projects, project]);
    }

    const updateProject = (updatedProj) => {
        const updateFunc = proj => {
            if (proj.project_id === updatedProj.project_id) {
                return updatedProj;
            }
            return proj;
        }

        const newProjects = projects.map(updateFunc);
        setProjects(newProjects);
    }

    const updateClass = (updatedClass) => {
        selectedClass.name = updatedClass.name;
        selectedClass.startSemester = updatedClass.sd1_term;
        selectedClass.year = updatedClass.sd1_year;
        selectedClass.coordinators = updatedClass.coordinators; 
    }

    async function handleEditProjectOpen(event, project) {
        event.stopPropagation();
        setSelectedProject(project);

        setEditProjectOpen(true);
    }

    async function handleEditStudentOpen(event, student) {
        event.stopPropagation();
        setSelectedStudent(student);
        if (student.group_id !== null) {
            await findStudentTeam(student.group_id);
        }
        setEditStudentOpen(true);
    }

    // gets the name of the team the student is currently in
    async function findStudentTeam(group_id) {
        const response = await fetch(`${process.env.REACT_APP_API_HOST}/api/groups/findGroup/${group_id}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${state.token.access_token}`,
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
        })
        const team = await response.json();
        setStudentCurrTeam(team);
    }

    function changeStudentGroup(user_id, newGroup) {
        const data = 
        {
            user_id: user_id,
            group_id: newGroup.group_id,
        };
        const updatedStudentList = students.map(student => {
            if (student.user_id === user_id)
                return {...student, group_id: newGroup.group_id};
            return student;
        })
        setStudents(updatedStudentList);
        fetch(`${process.env.REACT_APP_API_HOST}/api/user/updateGroup`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${state.token.access_token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data),
        })
            .then(res => res.json())
            .catch(error => console.error(error))
    }

    function showStudentDeleteModal(event, student) {
        event.stopPropagation();
        setSelectedStudent(student);

        const newProps = {
            type: 'student',
            content: student.first_name + ' ' + student.last_name,
            onDelete: () => {deleteStudent(student.user_id)}
        }
        setDynamicDeleteModalProps(newProps);

        setDeleteOpen(true);
    }

    function showProjectDeleteModal(event, project) {
        event.stopPropagation();
        setSelectedProject(project);

        const newProps = {
            type: 'project',
            content: project.name,
            onDelete: () => {deleteProject(project.project_id)}
        }
        setDynamicDeleteModalProps(newProps);

        setDeleteOpen(true);
    }

    function deleteProject(projectID) {

        fetch(`${process.env.REACT_APP_API_HOST}/api/file/removeAllProjectFiles/${projectID}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${state.token.access_token}`,
                'Content-Type': 'application/json'
            }
        })

        fetch(`${process.env.REACT_APP_API_HOST}/api/projects/delete/${projectID}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${state.token.access_token}`,
                'Content-Type': 'application/json'
            }
        })
            .then(res => res.ok && loadProjectData())
            .catch(error => console.error(error))

    }

    function deleteStudent(user_id) {
        fetch(`${process.env.REACT_APP_API_HOST}/api/class/deleteStudent/${user_id}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${state.token.access_token}`,
                'Content-Type': 'application/json'
            }
        })
            .then(res => res.ok && loadStudentData())
            .catch(error => console.error(error))
    }

    // api call, delete the class
    function deleteClass() {
        fetch(`${process.env.REACT_APP_API_HOST}/api/class/deleteClass/${state.class_id}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${state.token.access_token}`,
                'Content-Type': 'application/json'
            }
        })
            .then(res => res.ok && navigate('/classselect'))
            .catch(error => console.error(error))
    }

    function loadStudentData() {
        function initStudentData(data) {
            setStudents(data);
        }

        fetch(`${process.env.REACT_APP_API_HOST}/api/class/getStudents/${state.class_id}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${state.token.access_token}`,
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
        })
            .then(res => res.json())
            .then(j => initStudentData(j))
            .catch(error => console.error(error))
    }

    function loadProjectData() {
        function initProjectData(data) {
            setProjects(data);
        }
        fetch(`${process.env.REACT_APP_API_HOST}/api/projects/getAllForClass/${state.class_id}`, {
            method:'GET',
            headers: {Authorization: `Bearer ${state.token.access_token}`}
        })
            .then(res => res.json())
            .then(j => initProjectData(j))
            .catch(error => console.error(error));
    }

    const ProjectList = ({item}) => {
        const [projectDetailOpen, setProjectDetailOpen] = useState(false);
        
        const handleProjectDetailOpen =()=>{
            setProjectDetailOpen(true)
        }
        const handleProjectDetailClose =()=>{
            setProjectDetailOpen(false)
        }
    
        return(
            <div>
                <ProjectDetailModal open={projectDetailOpen} handleClose={handleProjectDetailClose} data={item}/> 
                <Stack direction='row' spacing={3} style={{ cursor: 'pointer', backgroundColor: '#ECF7FF', paddingInline: 10, paddingBlock: 3, borderRadius: 8, alignItems: 'center' }} onClick={(event) => handleProjectDetailOpen(event)}>
                    <Typography style={{ width: '60%', wordBreak: "break-all" }}>{item.name}</Typography>
                    <Typography style={{ width: '20%' }}>{item.end_semester}</Typography>
                    <Stack direction='row' sx={{ width: '20%', justifyContent: 'right' }}>
                        <IconButton onClick={event => handleEditProjectOpen(event, item)}><EditIcon /></IconButton>
                        <IconButton onClick={event => showProjectDeleteModal(event, item)}><DeleteIcon /></IconButton>
                    </Stack>
                </Stack>
            </div>
        )
    }

    useEffect(() => {
        loadProjectData();
        loadStudentData();
    }, [state]);

    return (
        <Box>
            <DashboardNavbar userType="admin"/>
            <Box sx={{ px: 3, my: 3, marginInline: "auto", maxWidth: 1400 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{display: "flex", gap: 2}}>
                        <Button variant='outlined' sx={{height: "2rem", alignSelf: "center"}} onClick={() => navigate("/classselect")} startIcon={<ArrowBackIcon/>}>
                            Classes
                        </Button>
                        <Typography variant='h4'>
                            {
                                selectedClass.name !== undefined ? selectedClass.name  + " - " + selectedClass.startSemester + " " + selectedClass.year
                                :
                                selectedClass.startSemester + " " + selectedClass.year
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={0} sx={{ flex: {xs: 1, sm: 0} }}>
                        <Stack spacing={2} sx={{ width: {xs: "100%", sm: 250} }}>
                            <Button variant='contained' fullWidth style={{backgroundColor: '#2E2E2E'}} onClick={() => setCreateOpen(true)}>Add project</Button>
                            <CreateProjectModal token={state.token} open={createOpen} handleClose={() => setCreateOpen(false)} addProject={addProject} selectedClass={selectedClass} />
                            <Box>
                                <FormControlLabel control={<Checkbox onChange={(event) => setImportProjects(event.target.checked)} />} label={<Typography sx={{fontSize: "14px"}}>Import Projects with Students</Typography>} style={{fontSize: "12px"}}/>
                                <CSVImport 
                                    class_id={state.class_id}
                                    user_id={state.user_id}
                                    label={"Import Roster"}
                                    importProjects={importProjects}
                                    host={process.env.REACT_APP_API_HOST}
                                    loadStudentData={loadStudentData}
                                    onClose={loadStudentData}
                                />
                            </Box>
                            <Button variant='contained' fullWidth style={{backgroundColor: '#2E2E2E'}} onClick={() => navigate(`/group_formation`, {state: state})}>Form Project Teams</Button>
                            <CSVExport
                                class_id={state.class_id}
                                user_id={state.user_id} 
                                token={state.token}
                            />
                            <Button variant='contained' fullWidth style={{backgroundColor: '#2E2E2E'}} onClick={() => navigate("/assignments", { state: state })}>Assignments</Button>
                            <Button variant='contained' fullWidth style={{backgroundColor: '#2E2E2E'}} onClick={() => setEditClassOpen(true)}>Edit Class</Button>
                            <EditClassModal open={editClassOpen} closeModal={() => setEditClassOpen(false)} token={state.token} selectedClass={selectedClass} updateClass={updateClass} handleDelete={deleteClass}></EditClassModal>
                            <Typography style={{color: "red"}}>{errorMessage}</Typography>
                        </Stack>
                    </Grid>
                    <Grid item sx={{flex: 1}}>
                        <Stack spacing={1} >
                            <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "flex-start", gap: 3, mb: 2, flexWrap: "wrap" }}>
                                <ToggleButtonGroup
                                    color='primary'
                                    value={listType}
                                    exclusive
                                    onChange={handleListChange}
                                >
                                    <ToggleButton value='projects'>Projects</ToggleButton>
                                    <ToggleButton value='students'>Students</ToggleButton>
                                </ToggleButtonGroup>
                                <TextField
                                    variant='outlined'
                                    size='small'
                                    label='Search'
                                    sx={{flex: 1, minWidth: 200}}
                                    InputProps={{
                                        endAdornment: <InputAdornment position='end'><SearchIcon></SearchIcon></InputAdornment>,
                                    }}
                                    onChange={e => setSearchFilter(e.target.value)}
                                />
                            </Box>
                            {listType === 'projects' && filteredProjects.map(item => (
                                // TODO: once project view page is made and implemented, fix the navigate from /placeholder to /projectview
                                <ProjectList key={'Project' + item.project_id} item={item}/>
                            ))}
                            {listType !== 'projects' && filteredStudents.map(item => (
                                <Stack direction='row' spacing={3} key={'student' + item.user_id} style={{ cursor: 'pointer', backgroundColor: '#ECF7FF', paddingInline: 10, paddingBlock: 3, borderRadius: 8, alignItems: 'center' }} onClick={() => navigate('/studentinfo', { state: { user_id: item.user_id, token: state.token } })}>
                                    <Typography style={{ width: '60%', wordBreak: "break-all" }}>{item.first_name} {item.last_name}</Typography>
                                    <Typography style={{ width: '20%' }}>{item.sd2_semester}</Typography>
                                    <Stack direction='row' sx={{ width: '20%', justifyContent: 'right' }}>
                                        <IconButton onClick={event => handleEditStudentOpen(event, item)}><EditIcon /></IconButton>
                                        <IconButton onClick={event => showStudentDeleteModal(event, item)}><DeleteIcon /></IconButton>
                                    </Stack>
                                </Stack>
                            ))}
                            <DeleteConfirmModal open={deleteOpen} handleClose={event => { event.stopPropagation(); setDeleteOpen(false); }} type={dynamicDeleteModalProps.type} content={dynamicDeleteModalProps.content} handleDelete={dynamicDeleteModalProps.onDelete} />
                            <EditProjectModal open={editProjectOpen} handleClose={() => setEditProjectOpen(false)} tokens={state.token} project={selectedProject} updateProject={updateProject} selectedClass={selectedClass} />
                            <EditStudentModal open={editStudentOpen} handleClose={() => setEditStudentOpen(false)} tokens={state.token} student={selectedStudent} currentTeam={studentCurrTeam} projects={projects} handleChangeGroup={changeStudentGroup} />
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

export default ClassView;